import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

type ChatStreamContextType = {
  isStreamInProgress: boolean;
  setIsStreamInProgress: (isStreaming: boolean) => void;
  /**
   * Hands over the way to stop the stream this page is showing, and takes it
   * back on `null`.
   *
   * Taking it back is not optional. The provider sits above the router, so a
   * function left behind by an unmounted conversation stays reachable from
   * every other page — and it stops, and *declares stopped*, a conversation
   * the reader has since navigated away from.
   */
  registerStopFunction: (stopFn: (() => void) | null) => void;
  stopStreamBeforeSend: () => Promise<void>;
};

const ChatStreamContext = createContext<ChatStreamContextType | undefined>(
  undefined
);

export const ChatStreamProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isStreamInProgress, setIsStreamInProgress] = useState<boolean>(false);
  const stopStreamFnRef = useRef<(() => void) | null>(null);
  // Mirror `isStreamInProgress` into a ref so `stopStreamBeforeSend` can
  // read the latest value without re-creating the callback on every state
  // change — re-creating it cascades into consumer re-renders and can
  // trigger React #185 (Maximum update depth exceeded) on the chat page.
  const isStreamInProgressRef = useRef(isStreamInProgress);
  isStreamInProgressRef.current = isStreamInProgress;

  const registerStopFunction = useCallback((stopFn: (() => void) | null) => {
    stopStreamFnRef.current = stopFn;
  }, []);

  const stopStreamBeforeSend = useCallback(async () => {
    if (isStreamInProgressRef.current && stopStreamFnRef.current) {
      stopStreamFnRef.current();
      // Small delay to let the stream properly stop before sending new message
      await new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 100);
      });
      setIsStreamInProgress(false);
    }
  }, []);

  const value = useMemo<ChatStreamContextType>(
    () => ({
      isStreamInProgress,
      setIsStreamInProgress,
      registerStopFunction,
      stopStreamBeforeSend,
    }),
    [isStreamInProgress, registerStopFunction, stopStreamBeforeSend]
  );

  return (
    <ChatStreamContext.Provider value={value}>
      {children}
    </ChatStreamContext.Provider>
  );
};

export const useChatStream = (): ChatStreamContextType => {
  const context = useContext(ChatStreamContext);
  if (!context) {
    throw new Error("useChatStream must be used within a ChatStreamProvider");
  }
  return context;
};
